import { currencyFormat } from './currencyFormat'

/**
 * Format a number as currency (using the existing currencyFormat utility)
 * @param amount - The amount to format
 * @param currency - The currency code (default: EUR)
 * @param locale - The locale (default: de-DE)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency = 'EUR', locale = 'de-DE'): string => {
  return currencyFormat(amount, currency, locale)
}

/**
 * Format a date string to a localized date
 * @param dateString - The date string to format
 * @param locale - The locale (default: en-US)
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, locale = 'en-US'): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Format a percentage value
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`
}

/**
 * A task's display label: its key merged into the position/activity name (issue #89).
 * The key is an optional field, so a task without one shows the bare name.
 * Matches the budget module's existing rendering (key, space, name).
 * Older projects typed the key into the name itself, so a name that already starts
 * with its key is returned as is rather than doubled (issue #135).
 * @param key - the task's numeric key, when it has one
 * @param name - the position or activity name
 * @returns the merged label
 * @example
 * taskDisplayName('1.234', 'Internal Training') // '1.234 Internal Training'
 * taskDisplayName(undefined, 'Internal Training') // 'Internal Training'
 */
export const taskDisplayName = (key: string | undefined | null, name: string): string => {
  return key && !name.startsWith(`${key} `) ? `${key} ${name}` : name
}